import { memo, useContext } from 'react';
import { useRouter } from 'next/router';
import { ThemeProvider, type Theme } from '@emotion/react';

import useThemeStyles from '../../lib/useThemeStyles';
import { isClient } from '../../lib/utils';
import Layout from '../Layout';
import PageContents from '../PageContents';
import PageContentsSkeleton from '../PageContentsSkeleton';
import { WWContext, type WWContextValue } from '../WWContext';
import RegistryTemplate from './RegistryTemplate';

import { isWwsWeddingWebsiteV2, type Wws_Page } from '../../types/graphql';
import MobileHomePageNav from '../MobileHomePageNav';
import MultiPagePageContents from '../MultiPagePageContents';
import { useWWSQueryContext } from '../../context/WWSQueryContext';

export interface TemplateProps {
  themeOverride: string;
}

function BaseTemplate({ themeOverride }: TemplateProps) {
  const {
    replace,
    query: { route, pathPrefix, slug, preview = 'true' },
  } = useRouter();

  const { data } = useContext<WWContextValue>(WWContext);
  const { isFullSiteDataLoaded, initialRouteName } = useWWSQueryContext();

  const theme: Partial<Theme> = useThemeStyles(themeOverride);

  const routeName = Array.isArray(route) ? route[0] : !route ? '' : route;

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const currentPage = data?.weddingWebsiteResult.pages?.find(
    (page: Wws_Page): boolean => page.routeName === routeName
  );

  // If any page besides page with routeName = / is hidden redirect
  if (
    !currentPage ||
    (currentPage &&
      currentPage.routeName !== '' &&
      !preview &&
      !currentPage.show)
  ) {
    // Next/Router can only be used client side, do the routing client side
    if (isClient()) {
      replace(`/${pathPrefix}/${slug}`);

      return null;
    }

    return null;
  }

  const isHomePage =
    currentPage.routeName === '' || currentPage.routeName === 'home';

  const hero: string | null | undefined = isHomePage
    ? data?.weddingWebsiteResult.coverPhoto?.path
    : undefined;

  const landscapeCrop = data?.weddingWebsiteResult.coverPhoto?.cropBox;

  const isRouteDataReady =
    (routeName || '') === initialRouteName || isFullSiteDataLoaded;

  return (
    <ThemeProvider theme={theme}>
      <Layout isHomePage={isHomePage}>
        <>
          {/* To avoid future CLS issues add all page content to either MultiPagePageContents or as a child of it */}
          <MultiPagePageContents
            landscapeCrop={landscapeCrop}
            heroSrc={hero}
            isHomePage={isHomePage}
          >
            {isRouteDataReady ? (
              <PageContents
                page={currentPage}
                photoAlbums={data?.weddingWebsiteResult.photoAlbums}
              />
            ) : (
              <PageContentsSkeleton />
            )}
          </MultiPagePageContents>
          {currentPage.type === 'WeddingPage' ? <MobileHomePageNav /> : null}
          {/**
           * Instead of conditionally rendering registry content,
           * the RegistryTemplate component uses
           * conditional styles to hide it when the user is
           * not on the /registry route.
           *
           * This prevents the injected registry/gvr script
           * from re-firing every time a user navigates to
           * the /registry route.
           */}
          <RegistryTemplate />
        </>
      </Layout>
    </ThemeProvider>
  );
}

export default memo(BaseTemplate);
